import { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';

import qs from 'query-string';

import API from 'core/Api';

import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity } from 'utils/helpers/client';

import InnerWrapper from 'routes/client/reports/InnerWrapper';
import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import NotAuthorizedScreen from 'routes/not-authorized';
import ReportForm from './components/ReportForm';
import LeavesAvailabilityTable from './components/LeavesAvailabilityTable';
import { LeavesAvailabilityDropdowns, LeavesAvailabilityReportFormData, LeavesAvailabilityReportParams } from './types';

const LeavesAvailability: FC = () => {
  const methods = useForm<LeavesAvailabilityReportFormData>();
  const openLinkBtn = useRef<HTMLAnchorElement>(null);
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const isOperator = getSubdomain() === 'operators';
  const isLeaveModuleActive = getUserInfo().featureLeavesIsActive;

  const [link, setLink] = useState<string | null>(null);
  const [reportParams, setReportParams] = useState<LeavesAvailabilityReportParams | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<LeavesAvailabilityDropdowns, Error>(
    [buildClientEntity('report-employee-paid-leaves-count-dropdowns', client)],
    (): Promise<LeavesAvailabilityDropdowns> => {
      return API.get('reports/employee/paid-leaves-count/dropdown-options').then(res => {
        return res.data[language || 'bg'];
      });
    }
  );

  const buildReportParams = (data: LeavesAvailabilityReportFormData): LeavesAvailabilityReportParams => {
    const entries: [string, any][] = [];

    const dateFrom = data.date_from ? formatDate(data.date_from, 'YYY/mm/dd') : null;
    const dateTo = data.date_to ? formatDate(data.date_to, 'YYY/mm/dd') : null;

    if (dateFrom) entries.push(['date_from', dateFrom]);
    if (dateTo) entries.push(['date_to', dateTo]);

    const userIds = data.user_id?.map(user => user.value);
    if (userIds?.length) entries.push(['user', userIds]);

    const groupIds = data.group_id?.map(group => group.value);
    if (groupIds?.length) entries.push(['group', groupIds]);

    const statusIds = data.status?.map(status => status.value);
    if (statusIds?.length) entries.push(['status', statusIds]);

    return Object.fromEntries(entries);
  };

  const handleSubmitReport = (data: LeavesAvailabilityReportFormData) => {
    setReportParams(buildReportParams(data));
  };

  const handleDownloadReport = async (data: LeavesAvailabilityReportFormData) => {
    setIsDownloading(true);
    try {
      const formattedData = buildReportParams(data);
      const queryString = qs.stringify(formattedData, { arrayFormat: 'bracket', skipNull: true });
      const res = await API.get(`reports/employee/paid-leaves-count/download?${queryString}`);
      setLink(res.data.url);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (link) openLinkBtn?.current?.click();
  }, [link]);

  if (isLeaveModuleActive || isOperator) {
    return (
      <InnerWrapper
        isLoading={isLoadingDropdowns}
        error={errorDropdowns}
      >
        {dropdowns && (
          <ReportForm
            data={dropdowns}
            methods={methods}
            onSubmit={handleSubmitReport}
            onDownload={handleDownloadReport}
            isDownloading={isDownloading}
          />
        )}
        {reportParams && (
          <div className="mt-3">
            <LeavesAvailabilityTable reportParams={reportParams} />
          </div>
        )}
        {link && (
          <Link
            ref={openLinkBtn}
            to={{
              pathname: link,
            }}
            target="_blank"
            style={{ display: 'none' }}
          />
        )}
      </InnerWrapper>
    );
  }

  return <NotAuthorizedScreen />;
};

export default LeavesAvailability;
